/**
 * Helpers partagés pour les congés (page Congés et portail employé).
 */

export const TYPE_CONGE_LABELS = {
  cp: 'Congés payés',
  rtt: 'RTT',
  anciennete: 'Congé ancienneté',
  maladie: 'Arrêt maladie',
  exceptionnel: 'Congé exceptionnel',
  sans_solde: 'Sans solde',
  recuperation: 'Récupération',
  formation: 'Formation',
  autre: 'Autre'
};

export const STATUT_CONGE_LABELS = {
  demande: 'En attente',
  valide_manager: 'Validé manager',
  valide_rh: 'Validé RH',
  refuse: 'Refusé'
};

/**
 * Applique les demi-journées au nombre de jours calculé : -0,5 jour par option, minimum 0,5.
 * Renvoie la valeur d'origine si elle n'est pas un nombre positif.
 * (Même règle que apply_demi_journees côté backend.)
 */
export function applyDemiJournees(n, demiDebut, demiFin) {
  const v0 = parseFloat(n);
  if (Number.isNaN(v0) || v0 <= 0) return n;
  let v = v0;
  if (demiDebut) v -= 0.5;
  if (demiFin) v -= 0.5;
  return Math.max(v, 0.5);
}

/**
 * Nombre de jours calendaires (bornes incluses). Utilisé en secours si l'API ne répond pas.
 */
export function calculateCalendarDays(start, end) {
  if (!start || !end) return '';
  const startDate = new Date(start + 'T00:00:00');
  const endDate = new Date(end + 'T00:00:00');
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '';
  const diffTime = Math.abs(endDate - startDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
}

/** 'YYYY-MM-DD' -> 'JJ/MM' */
export function formatJourMois(dateStr) {
  if (!dateStr || typeof dateStr !== 'string' || dateStr.length < 10) return dateStr || '';
  return `${dateStr.slice(8, 10)}/${dateStr.slice(5, 7)}`;
}

/**
 * True si la journée de solidarité (déclarée travaillée) est comprise dans [start, end].
 * `config` : { journee_solidarite_date, journee_solidarite_travaillee }
 */
export function isJourneeSolidariteInPeriod(config, start, end) {
  if (!config || !config.journee_solidarite_travaillee || !config.journee_solidarite_date) return false;
  if (!start || !end) return false;
  const js = config.journee_solidarite_date;
  return start <= js && js <= end;
}

/** Message d'information affiché dans les formulaires de demande. */
export function journeeSolidariteMessage(dateStr) {
  return `La journée de solidarité (${formatJourMois(dateStr)}) est travaillée : elle est décomptée.`;
}

/**
 * Détermine le message "journée de solidarité" à afficher pour une période, à partir
 * de la réponse de /conges/calculate-days (journee_solidarite_incluse, journee_solidarite_date)
 * ou, à défaut, de la configuration structure.
 */
export function getSolidariteInfo(daysInfo, config, start, end) {
  if (daysInfo && daysInfo.journee_solidarite_incluse && daysInfo.journee_solidarite_date) {
    return journeeSolidariteMessage(daysInfo.journee_solidarite_date);
  }
  if (isJourneeSolidariteInPeriod(config, start, end)) {
    return journeeSolidariteMessage(config.journee_solidarite_date);
  }
  return null;
}

/**
 * Nom de fichier extrait d'un header Content-Disposition (ou valeur par défaut).
 */
export function filenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) {
    try { return decodeURIComponent(utf8[1]); } catch { /* ignore */ }
  }
  const simple = /filename="?([^";]+)"?/i.exec(disposition);
  return simple ? simple[1] : fallback;
}

/**
 * Déclenche le téléchargement d'un blob dans le navigateur.
 */
export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}
